import React, { useId } from 'react'

export interface AiAssistantOrbProps {
  size: number
  className?: string
  processing?: boolean
}

const animationStyles = `
  .ai-assistant-orb__spark-main {
    animation: ai-assistant-orb-spark-main 4.8s ease-in-out infinite;
    transform-box: fill-box;
    transform-origin: center;
  }
  .ai-assistant-orb__spark-small-a {
    animation: ai-assistant-orb-spark-small 6.4s ease-in-out -1.7s infinite;
    transform-box: fill-box;
    transform-origin: center;
  }
  @keyframes ai-assistant-orb-spark-main {
    0%, 18%, 100% { transform: translate(0, .25px) scale(.9, .94); opacity: .62; }
    38% { transform: translate(.25px, -.25px) scale(1.06, 1.02); opacity: 1; }
    58%, 82% { transform: translate(-.2px, .1px) scale(.96, 1.03); opacity: .78; }
  }
  @keyframes ai-assistant-orb-spark-small {
    0%, 26%, 100% { transform: translate(0, .35px) scale(.72, .82); opacity: .22; }
    45% { transform: translate(.35px, -.25px) scale(1.08, 1); opacity: .82; }
    65%, 84% { transform: translate(-.2px, 0) scale(.86, .94); opacity: .38; }
  }

  .ai-assistant-orb__fluid-a {
    animation: ai-assistant-orb-fluid-a 5.2s ease-in-out infinite alternate;
    transform-box: view-box;
    transform-origin: 16px 16px;
  }
  .ai-assistant-orb__fluid-b {
    animation: ai-assistant-orb-fluid-b 7.4s ease-in-out -2.6s infinite alternate;
    transform-box: view-box;
    transform-origin: 16px 16px;
  }
  .ai-assistant-orb__fluid-c {
    animation: ai-assistant-orb-fluid-c 9.6s ease-in-out -5.1s infinite alternate;
    transform-box: view-box;
    transform-origin: 16px 16px;
  }
  @keyframes ai-assistant-orb-fluid-a {
    0% { transform: translate(-1.2px, .7px) scale(.88, 1.12); opacity: .52; }
    46% { transform: translate(1.35px, -.9px) scale(1.12, .9); opacity: .82; }
    100% { transform: translate(.25px, 1.1px) scale(.95, 1.08); opacity: .62; }
  }
  @keyframes ai-assistant-orb-fluid-b {
    0% { transform: translate(1px, -1px) scale(1.1, .9); opacity: .5; }
    52% { transform: translate(-1.4px, .45px) scale(.9, 1.13); opacity: .76; }
    100% { transform: translate(.55px, 1.2px) scale(1.06, .94); opacity: .58; }
  }
  @keyframes ai-assistant-orb-fluid-c {
    0% { transform: translate(-.7px, -1.15px) scale(.94, 1.08); opacity: .35; }
    58% { transform: translate(1.25px, .8px) scale(1.1, .92); opacity: .62; }
    100% { transform: translate(-1.1px, .2px) scale(.9, 1.1); opacity: .44; }
  }

  [data-ai-assistant-orb-processing="true"] .ai-assistant-orb__spark-main,
  [data-ai-assistant-orb-processing="true"] .ai-assistant-orb__fluid-a {
    animation-duration: 1.25s;
  }
  [data-ai-assistant-orb-processing="true"] .ai-assistant-orb__spark-small-a,
  [data-ai-assistant-orb-processing="true"] .ai-assistant-orb__fluid-b {
    animation-duration: 1.65s;
  }
  [data-ai-assistant-orb-processing="true"] .ai-assistant-orb__fluid-c {
    animation-duration: 2.1s;
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-assistant-orb__spark-main,
    .ai-assistant-orb__spark-small-a,
    .ai-assistant-orb__fluid-a,
    .ai-assistant-orb__fluid-b,
    .ai-assistant-orb__fluid-c {
      animation: none;
      transform: none;
    }
  }
`

const LargeSpark = ({ className = '' }: { className?: string }) => (
  <path className={className} data-ai-assistant-orb-spark="main" d="M16 6.8C16.55 11.35 18.65 13.45 23.2 14C18.65 14.55 16.55 16.65 16 21.2C15.45 16.65 13.35 14.55 8.8 14C13.35 13.45 15.45 11.35 16 6.8Z" fill="#FFFFFF" />
)

const SmallSpark = ({ className = '', x, y, scale = 1 }: { className?: string, x: number, y: number, scale?: number }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <path className={className} data-ai-assistant-orb-spark="secondary" d="M0 -3C.28 -1.1 1.1 -.28 3 0C1.1.28.28 1.1 0 3C-.28 1.1-1.1.28-3 0C-1.1-.28-.28-1.1 0-3Z" fill="#DEE2ED" />
  </g>
)

const FluidLayers = ({ blueGlowId, whiteGlowId }: { blueGlowId: string, whiteGlowId: string }) => (
  <>
    <ellipse data-ai-assistant-orb-fluid-layer="true" className="ai-assistant-orb__fluid-a" cx="13" cy="17" rx="9.5" ry="7" fill={`url(#${blueGlowId})`} />
    <ellipse data-ai-assistant-orb-fluid-layer="true" className="ai-assistant-orb__fluid-b" cx="19" cy="13" rx="8.5" ry="6.5" fill={`url(#${whiteGlowId})`} />
    <ellipse data-ai-assistant-orb-fluid-layer="true" className="ai-assistant-orb__fluid-c" cx="17" cy="20" rx="6.5" ry="5" fill="#AFC5FF" fillOpacity=".35" />
  </>
)

const HybridMotif = ({ blueGlowId, whiteGlowId }: { blueGlowId: string, whiteGlowId: string }) => (
  <g data-ai-assistant-orb-motif="hybrid">
    <FluidLayers blueGlowId={blueGlowId} whiteGlowId={whiteGlowId} />
    <LargeSpark className="ai-assistant-orb__spark-main" />
    <SmallSpark className="ai-assistant-orb__spark-small-a" x={23.1} y={21.2} scale={0.78} />
  </g>
)

export default function AiAssistantOrb({ size, className, processing = false }: AiAssistantOrbProps) {
  const instanceId = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const clipId = `ai-assistant-orb-clip-${instanceId}`
  const coreId = `ai-assistant-orb-core-${instanceId}`
  const blueGlowId = `ai-assistant-orb-blue-${instanceId}`
  const whiteGlowId = `ai-assistant-orb-white-${instanceId}`
  const shadowId = `ai-assistant-orb-shadow-${instanceId}`

  return (
    <svg className={className} width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" data-ai-assistant-orb="true" data-ai-assistant-orb-variant="hybrid" data-ai-assistant-orb-processing={processing || undefined} style={{ display: 'block' }}>
      <style>{animationStyles}</style>
      <defs>
        <clipPath id={clipId}><circle cx="16" cy="16" r="16" /></clipPath>
        <radialGradient id={coreId} data-ai-assistant-orb-core-gradient="true" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(10 8) rotate(48) scale(27)">
          <stop stopColor="#7199F4" /><stop offset=".46" stopColor="#275FE0" /><stop offset="1" stopColor="#173B94" />
        </radialGradient>
        <radialGradient id={blueGlowId} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(9 23) rotate(-30) scale(18 14)">
          <stop stopColor="#8CB0FF" stopOpacity=".9" /><stop offset=".52" stopColor="#275FE0" stopOpacity=".4" /><stop offset="1" stopColor="#275FE0" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={whiteGlowId} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(23 7) rotate(138) scale(16 12)">
          <stop stopColor="#FFFFFF" stopOpacity=".76" /><stop offset=".5" stopColor="#DEE2ED" stopOpacity=".2" /><stop offset="1" stopColor="#DEE2ED" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={shadowId} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(26 24) rotate(-142) scale(17 15)">
          <stop stopColor="#102B6D" stopOpacity=".62" /><stop offset=".56" stopColor="#173B94" stopOpacity=".26" /><stop offset="1" stopColor="#173B94" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="16" cy="16" r="16" fill={`url(#${coreId})`} />
      <g clipPath={`url(#${clipId})`}>
        <ellipse cx="8" cy="24" rx="17" ry="11" fill={`url(#${blueGlowId})`} opacity=".45" />
        <ellipse cx="25" cy="24" rx="14" ry="12" fill={`url(#${shadowId})`} opacity=".38" />
        <ellipse cx="23" cy="7" rx="13" ry="9" fill={`url(#${whiteGlowId})`} opacity=".34" />
        <HybridMotif blueGlowId={blueGlowId} whiteGlowId={whiteGlowId} />
      </g>
    </svg>
  )
}
